import _ from "lodash";
import React from "react";

import { Badge } from "@toddle-edu/ds-web";

import { getMemoizedLangFunction } from "Utils";
import { CURRICULUM_TYPE_ORDER_MAPPING } from "Constants";
import ACLStore from "lib/aclStore";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { UNIT_FLOW_REVAMP_PERMISSION } from "Constants/permissionConstants";
import {
  showDPLibraryForUBD,
  isLEorUnitPlanView,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { getIsLearningCoursesEnabledForOrgForCurrentAY } from "LearningCourses/modules/Utils";

const WS_QUESTION_IMPORT_ORDER_MAPPING = {
  [CURRICULUM_TYPE_DP]: 0,
  [CURRICULUM_TYPE_MYP]: 1,
  [CURRICULUM_TYPE_UBD]: 2,
  [CURRICULUM_TYPE_PYP]: 3,
};

const GRADE_11 = "Grade 11";
const GRADE_12 = "Grade 12";

const generateOptionKey = grade => {
  const { id: gradeId, curriculumProgram = {} } = grade;

  const curriculumProgramType = _.get(curriculumProgram, "type", "");
  const key = `${gradeId}-${curriculumProgramType}`;

  return key;
};

const getDataGroupedByCurriculum = ({
  data,
  curriculumTypes,
  isDPLibrary,
  t,
}) => {
  const groupedByCurriculum = _.groupBy(data, item => {
    const {
      curriculumProgram: { id },
    } = item;
    return id;
  });

  const isSingleCurriculum = _.size(groupedByCurriculum) === 1;

  const formattedData = _.reduce(
    groupedByCurriculum,
    (result, groupedValues) => {
      const curriculumProgramType = _.get(
        _.first(groupedValues),
        "curriculumProgram.type"
      );

      if (!_.includes(curriculumTypes, curriculumProgramType)) {
        return result;
      }

      const acronym = _.get(
        _.first(groupedValues),
        "curriculumProgram.acronym"
      );

      const label = isSingleCurriculum ? t("common:grade_plural") : acronym;

      let UBDHigherGrades;
      const isUBD = _.isEqual(curriculumProgramType, CURRICULUM_TYPE_UBD);
      const shouldFilterUBDHigherGrades = isUBD && isDPLibrary;

      if (shouldFilterUBDHigherGrades) {
        UBDHigherGrades = _.filter(groupedValues, ({ globalGrade }) =>
          _.includes([GRADE_11, GRADE_12], globalGrade.name)
        );
      }

      result.push({
        label,
        options: shouldFilterUBDHigherGrades ? UBDHigherGrades : groupedValues,
        curriculumProgramType,
      });

      return result;
    },
    []
  );

  const mappingOrder = isDPLibrary
    ? WS_QUESTION_IMPORT_ORDER_MAPPING
    : CURRICULUM_TYPE_ORDER_MAPPING;

  const sortedData = _.orderBy(
    formattedData,
    [obj => mappingOrder[obj.curriculumProgramType]],
    "asc"
  );
  return sortedData;
};

const getGradeOptions = ({
  t,
  gradeList,
  isDPLibrary,
  isDPSchoolLibrary,
  isMypGrade,
}) => {
  const DPLibraryTypes =
    showDPLibraryForUBD() && !isDPSchoolLibrary
      ? [CURRICULUM_TYPE_DP, CURRICULUM_TYPE_UBD]
      : [CURRICULUM_TYPE_DP];
  let curriculumTypes;
  if (isDPLibrary || isDPSchoolLibrary) {
    curriculumTypes = DPLibraryTypes;
  } else if (isMypGrade && !isLEorUnitPlanView()) {
    curriculumTypes = [CURRICULUM_TYPE_MYP];
  } else {
    curriculumTypes = [
      CURRICULUM_TYPE_PYP,
      CURRICULUM_TYPE_UBD,
      CURRICULUM_TYPE_MYP,
    ];
  }
  const groupedGradeList = getDataGroupedByCurriculum({
    data: gradeList,
    curriculumTypes,
    isDPLibrary,
    t,
  });

  // tier-3: Root
  const options = [
    {
      key: "root",
      children: _.map(groupedGradeList, "label"),
    },
  ];

  let defaultOption;

  _.forEach(groupedGradeList, (curriculum, groupedGradeListIndex) => {
    const { label, options: gradeOptions } = curriculum;
    const acronymKey = label;

    const curriculumAcronymOption = {
      key: acronymKey,
      title: label,
      tier: 2,
      parent: "root",
      listType: "accordion",
      children: _.map(gradeOptions, grade => generateOptionKey(grade)),
    };

    // Expand the first curriculum list by default
    if (groupedGradeListIndex == 0) {
      curriculumAcronymOption["isExpanded"] = true;
    }

    // tier-2: Curriculum acronyms
    options.push(curriculumAcronymOption);

    _.forEach(gradeOptions, (grade, gradeIndex) => {
      const { name: gradeName, unitPlanCount } = grade;
      const gradeKey = generateOptionKey(grade);

      const countComponent = (
        <Badge
          type="numeric"
          size={"medium"}
          variant={"subtle"}
          value={unitPlanCount}
          showZero={"true"}
        />
      );

      const gradeOption = {
        key: gradeKey,
        title: gradeName,
        tier: 1,
        parent: acronymKey,
        children: [],
        suffix: countComponent,
      };

      // Disable the grade option if there are no unit plans
      if (!unitPlanCount) {
        gradeOption["isDisabled"] = true;
      }

      if (groupedGradeListIndex == 0 && gradeIndex == 0) {
        defaultOption = gradeOption;
      }

      // tier-1: Grades
      options.push(gradeOption);
    });
  });

  return { gradeOptions: options, defaultOption };
};

export const getGradeOptionsMemoized = getMemoizedLangFunction(getGradeOptions);

export const getUnitPlanFeedList = unitPlans => {
  const { edges = [] } = unitPlans;
  let unitPlanNodes = _.map(edges, "node");
  unitPlanNodes = _.filter(unitPlanNodes, ({ title }) => !_.isEmpty(title));

  const unitPlanFeedList = _.map(unitPlanNodes, unitPlan => {
    const {
      id,
      title = {},
      image = {},
      subjects = {},
      curriculumProgram = {},
      unitFlowFeed = {},
      courses = [],
    } = unitPlan;

    const titleValue = _.get(title, "value", "");
    const imageUrl = _.get(image, "value", "");
    const subjectsList = _.get(subjects, "resolvedMinimalTree.subjects", []);
    const unitFlowId = _.get(unitFlowFeed, "unitFlowId", null);

    return {
      id,
      title: titleValue,
      imageUrl,
      subjects: subjectsList,
      unitFlowId,
      curriculumProgram,
      courses,
    };
  });

  return unitPlanFeedList;
};

export const getUnitPlanQueryVariables = ({
  orgId,
  gradeId,
  filters,
  academicYears,
}) => {
  const { unitTypes, subjects, creators, courses, searchText } = filters;
  const isLearningCoursesEnabledForOrg = getIsLearningCoursesEnabledForOrgForCurrentAY();
  const getUnitFlowId =
    ACLStore.can(UNIT_FLOW_REVAMP_PERMISSION) &&
    !isLearningCoursesEnabledForOrg;

  const queryFilters = {
    courses,
    searchText,
    grades: [],
    unitType: unitTypes,
    subjects: subjects,
    createdBy: creators,
    academicYears: academicYears ?? [],
  };

  if (gradeId) {
    queryFilters["grades"] = [gradeId];
  }

  const unitPlanFeedQueryVariables = {
    id: orgId,
    filters: queryFilters,
    getUnitFlowId,
    portalType: "PLANNER", // Need to pass this to send platform user JWT in request header
  };

  return unitPlanFeedQueryVariables;
};
